import React, { useState } from 'react';
import MultiSelect from './MultiSelect';

const CollapsibleSection = ({ title, isExpanded, onToggle, children }) => (
  <div className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
    >
      <span className="font-medium text-gray-900 dark:text-white">{title}</span>
      <svg
        className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    {isExpanded && (
      <div className="px-4 pb-4">
        {children}
      </div>
    )}
  </div>
);

const ParliamentMembersFilterSidebar = ({
  filters,
  onFiltersChange,
  hashtagOptions,
  partyOptions,
  onClearFilters,
  resultCounts,
  isMobileOpen = false,
  onClose
}) => {
  const [dateRangeExpanded, setDateRangeExpanded] = useState(true);
  const [hashtagsExpanded, setHashtagsExpanded] = useState(true);
  const [partiesExpanded, setPartiesExpanded] = useState(true);
  const [searchExpanded, setSearchExpanded] = useState(true);
  const [isCustomSelected, setIsCustomSelected] = useState(false);

  const handleDateRangeChange = (type, value) => {
    if (type === 'preset') {
      const now = new Date();
      let start = '';
      
      switch (value) {
        case 'last24hours':
          start = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          setIsCustomSelected(false);
          break;
        case 'last7days':
          start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          setIsCustomSelected(false);
          break;
        case 'last30days':
          start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          setIsCustomSelected(false);
          break;
        case 'last90days':
          start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          setIsCustomSelected(false);
          break;
        case 'custom':
          setIsCustomSelected(true);
          onFiltersChange({
            ...filters,
            dateRange: {
              start: '',
              end: ''
            }
          });
          return;
        default:
          start = '';
          setIsCustomSelected(false);
      }
      
      if (value !== 'custom') {
        onFiltersChange({
          ...filters,
          dateRange: {
            start,
            end: now.toISOString().split('T')[0]
          }
        });
      }
    } else {
      onFiltersChange({
        ...filters,
        dateRange: {
          ...filters.dateRange,
          [type]: value
        }
      });
    }
  };

  const handleHashtagsChange = (selectedHashtags) => {
    onFiltersChange({
      ...filters,
      selectedHashtags
    });
  };

  const handlePartiesChange = (selectedParties) => {
    onFiltersChange({
      ...filters,
      selectedParties
    });
  };


  const handleSearchChange = (searchText) => {
    onFiltersChange({
      ...filters,
      searchText
    });
  };

  const hasActiveFilters =
    filters.dateRange.start ||
    filters.dateRange.end ||
    filters.selectedHashtags.length > 0 ||
    filters.selectedParties.length > 0 ||
    filters.searchText;

  const getSelectedDateRange = () => {
    if (isCustomSelected) {
      return 'custom';
    }
    
    if (!filters.dateRange.start && !filters.dateRange.end) {
      return '';
    }
    
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const last90Days = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    if (filters.dateRange.start === yesterday && filters.dateRange.end === today) {
      return 'last24hours';
    } else if (filters.dateRange.start === last7Days && filters.dateRange.end === today) {
      return 'last7days';
    } else if (filters.dateRange.start === last30Days && filters.dateRange.end === today) {
      return 'last30days';
    } else if (filters.dateRange.start === last90Days && filters.dateRange.end === today) {
      return 'last90days';
    }
    
    return 'custom';
  };

  const shouldShowCustomInputs = () => {
    return isCustomSelected;
  };

  return (
    <div className={`w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 h-full overflow-y-auto transition-transform transition-colors duration-200 md:sticky md:top-16 md:translate-x-0 ${isMobileOpen ? 'translate-x-0 fixed inset-y-0 left-0 z-40' : '-translate-x-full fixed inset-y-0 left-0 z-40'}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Filters</h3>
        <div className="flex items-center space-x-2">
          {hasActiveFilters && (
            <button
              onClick={() => {
                setIsCustomSelected(false);
                onClearFilters();
              }}
              className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 font-medium transition-colors"
            >
              Clear All
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="md:hidden text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Search Section */}
      <CollapsibleSection
        title="Search"
        isExpanded={searchExpanded}
        onToggle={() => setSearchExpanded(!searchExpanded)}
      >
        <div className="space-y-3">
          <input
            type="text"
            value={filters.searchText || ''}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by name..."
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </CollapsibleSection>

      {/* Parties Section */}
      <CollapsibleSection
        title="Parties"
        isExpanded={partiesExpanded}
        onToggle={() => setPartiesExpanded(!partiesExpanded)}
      >
        <MultiSelect
          options={partyOptions}
          selectedValues={filters.selectedParties}
          onChange={handlePartiesChange}
          placeholder="Select parties..."
        />
      </CollapsibleSection>


      {/* Date Range Section */}
      <CollapsibleSection
        title="Activity Date Range"
        isExpanded={dateRangeExpanded}
        onToggle={() => setDateRangeExpanded(!dateRangeExpanded)}
      >
        <div className="space-y-3">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Filter by when members were last active
          </div>
          {/* Preset Options */}
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                name="dateRange"
                value="last24hours"
                checked={getSelectedDateRange() === 'last24hours'}
                onChange={(e) => handleDateRangeChange('preset', e.target.value)}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">Last 24 hours</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="radio"
                name="dateRange"
                value="last7days"
                checked={getSelectedDateRange() === 'last7days'}
                onChange={(e) => handleDateRangeChange('preset', e.target.value)}
                className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-blue-500"
              />
              <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">Last 7 days</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="radio"
                name="dateRange"
                value="last30days"
                checked={getSelectedDateRange() === 'last30days'}
                onChange={(e) => handleDateRangeChange('preset', e.target.value)}
                className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-blue-500"
              />
              <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">Last 30 days</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="radio"
                name="dateRange"
                value="last90days"
                checked={getSelectedDateRange() === 'last90days'}
                onChange={(e) => handleDateRangeChange('preset', e.target.value)}
                className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-blue-500"
              />
              <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">Last 90 days</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="radio"
                name="dateRange"
                value="custom"
                checked={getSelectedDateRange() === 'custom'}
                onChange={(e) => handleDateRangeChange('preset', e.target.value)}
                className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-blue-500"
              />
              <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">Custom range</span>
            </label>
          </div>

          {/* Custom Date Inputs */}
          {shouldShowCustomInputs() && (
            <div className="mt-4 space-y-3 pl-7">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={filters.dateRange.start || ''}
                  onChange={(e) => handleDateRangeChange('start', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="dd/mm/yyyy"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={filters.dateRange.end || ''}
                  onChange={(e) => handleDateRangeChange('end', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="dd/mm/yyyy"
                />
              </div>
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* Hashtags Section */}
      <CollapsibleSection
        title="Top Topics"
        isExpanded={hashtagsExpanded}
        onToggle={() => setHashtagsExpanded(!hashtagsExpanded)}
      >
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Filter by topics members have spoken about
        </div>
        <MultiSelect
          options={hashtagOptions}
          selectedValues={filters.selectedHashtags}
          onChange={handleHashtagsChange}
          placeholder="Select topics..."
        />
      </CollapsibleSection>

      {/* Results Section */}
      <div className="p-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Results</h4>
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <div className="flex justify-between">
            <span>Members:</span>
            <span className="font-medium text-gray-900 dark:text-white">{resultCounts.members}</span>
          </div>
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Active Filters</h4>
          <div className="space-y-2">
            {filters.searchText && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 mr-1 mb-1">
                Search: {filters.searchText}
                <button
                  onClick={() => handleSearchChange('')}
                  className="ml-1 text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-100"
                >
                  ×
                </button>
              </span>
            )}
            {filters.dateRange.start && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 mr-1 mb-1">
                From: {new Date(filters.dateRange.start).toLocaleDateString()}
                <button
                  onClick={() => handleDateRangeChange('start', '')}
                  className="ml-1 text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-100"
                >
                  ×
                </button>
              </span>
            )}
            {filters.dateRange.end && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 mr-1 mb-1">
                To: {new Date(filters.dateRange.end).toLocaleDateString()}
                <button
                  onClick={() => handleDateRangeChange('end', '')}
                  className="ml-1 text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-100"
                >
                  ×
                </button>
              </span>
            )}
            {filters.selectedParties.map(party => (
              <span key={party} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 mr-1 mb-1">
                {party}
                <button
                  onClick={() => handlePartiesChange(filters.selectedParties.filter(p => p !== party))}
                  className="ml-1 text-red-600 hover:text-red-800 dark:text-red-300 dark:hover:text-red-100"
                >
                  ×
                </button>
              </span>
            ))}
            {filters.selectedHashtags.map(hashtag => (
              <span key={hashtag} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 mr-1 mb-1">
                {hashtag}
                <button
                  onClick={() => handleHashtagsChange(filters.selectedHashtags.filter(h => h !== hashtag))}
                  className="ml-1 text-green-600 hover:text-green-800 dark:text-green-300 dark:hover:text-green-100"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ParliamentMembersFilterSidebar;
